import {PurchaseOrder, PurchaseOrderItem} from "../../../api/Logistics";
import {PhaseViewCol} from "../../project/shopdrawing/openings/PhaseCol";
import {
    actionsColName,
    AdjustCol,
    CentsCol,
    Group,
    NumberCol,
    StringCol,
    Table,
    TableContext,
    TableSelectionContext,
    ViewOnly,
} from "../../../misc/scroller/Table";
import {api} from "../../../api/API";
import {ItemsFooter} from "./ItemsFooter";
import React, {useContext, useEffect} from "react";
import {purchasingPopoutPanel} from "./PurchaseOrderList";
import {useSyncedRef} from "../../../misc/SyncedRef";
import {green, grey, red} from "@mui/material/colors";
import {getAll} from "../routes/useGetAll";
import {CheckboxContent, CheckboxProvider} from "../../project/shopdrawing/Checkbox";
import {ConfirmContext} from "../../../misc/Confirm";
import {useSnackbar} from "../../../misc/Snackbar";
import {EditContext} from "../../../misc/scroller/EditProvider";
import {MultiSelectRowHeader} from "../../project/quote/openings/MultiSelectRowHeader";
import {ListItemText, MenuItem} from "@mui/material";
import {RowActionsBase} from "../../project/quote/openings/RowActions";
import {DateFilterCol} from "../../../misc/DateFilterForCol";

import {purchaseOrderItemsChanged} from "./PurchaseOrderEvents";

export const purchaseOrderItemsTableName = "purchasing.purchase-order-items"

export function Items(props: {
    order: PurchaseOrder;
}) {
    const ctx = useContext(TableSelectionContext);
    const reloadRef = useSyncedRef(ctx.refresh)
    const orderIdRef = useSyncedRef(props.order.id);

    useEffect(() => {
        const sub = purchasingPopoutPanel.fromClient.subscribe(v => {
            if(v === "refresh-po") {
                reloadRef.current(purchaseOrderItemsTableName);
                purchaseOrderItemsChanged.emit(orderIdRef.current);
            }
        })

        return () => sub.cancel();
    }, [reloadRef]);

    const editable = props.order.status === "draft"
    const showFrames = props.order.supplierSellsFrames;
    const showDoors = props.order.supplierSellsDoors;

    return (
        <>
        <CheckboxProvider>
            <Table<PurchaseOrderItem>
                name={purchaseOrderItemsTableName}
                cellCustomize={{
                    backgroundColor: (row) => {
                        if(row.qtyOutstanding === 0) {
                            return grey["200"]
                        }

                        return "white";
                    }
                }}
                columns={[
                    editable && {
                        name: actionsColName,
                        noCsv: true,
                        render: o => <RowActions row={o} />,
                        renderName: () => <BulkActions />,
                        width: 100,
                    },
                    StringCol("Ref", "reference", 35),
                    AdjustCol(StringCol("Description", "description", 200), {
                        maxWidth: 600,
                    }),
                    ViewOnly(StringCol("Code", "code", 80)),
                    DateFilterCol<PurchaseOrderItem>("Due", "neededBy", "MMM D, YYYY", 100, "future"),

                    (showFrames || showDoors) && ViewOnly(StringCol("Opening", "openingName", 100)),

                    // Frame columns
                    showFrames && ViewOnly(StringCol("Floor", "openingFloor", 70)),
                    showFrames && PhaseViewCol<PurchaseOrderItem>("openingPhase", 70),
                    showFrames && ViewOnly(StringCol("F. Series", "frameSeries", 100)),
                    showFrames && ViewOnly(StringCol("F. Material", "frameMaterial", 70)),
                    showFrames && ViewOnly(StringCol("F. Gauge", "frameGauge", 70)),
                    showFrames && ViewOnly(StringCol("F. Screen Elev", "frameScreenElev", 100)),
                    showFrames && ViewOnly(StringCol("F. Profile", "frameProfile", 70)),
                    showFrames && ViewOnly(StringCol("F. Jamb Depth", "frameJambDepth", 100)),
                    showFrames && ViewOnly(StringCol("F. Door Thickness", "frameDoorThickness", 100)),
                    showFrames && ViewOnly(StringCol("F. Construction", "frameConstruction", 100)),
                    showFrames && ViewOnly(StringCol("F. Handing", "frameHanding", 70)),
                    showFrames && ViewOnly(StringCol("F. Hardware Preps", "frameHardwarePreps", 130)),
                    showFrames && ViewOnly(StringCol("F. Label", "frameLabel", 100)),
                    showFrames && ViewOnly(StringCol("F. Type", "frameType", 70)),

                    // Door columns
                    showDoors && ViewOnly(StringCol("D. Series", "doorSeries", 70)),
                    showDoors && ViewOnly(StringCol("D. Material", "doorMaterial", 100)),
                    showDoors && ViewOnly(StringCol("D. Finish", "doorFinish", 100)),
                    showDoors && ViewOnly(StringCol("D. Gauge", "doorGauge", 70)),
                    showDoors && ViewOnly(StringCol("D. Elev", "doorElevation", 70)),
                    showDoors && ViewOnly(StringCol("D. Core", "doorCore", 70)),
                    showDoors && ViewOnly(StringCol("D. Seam", "doorSeam", 70)),
                    showDoors && ViewOnly(StringCol("D. Handing", "doorHanding", 70)),
                    showDoors && ViewOnly(StringCol("D. Label", "doorLabel", 120)),

                    NumberCol("Qty", "qty", 60),
                    CentsCol("Unit Amount", "unitPriceCents"),
                    ViewOnly({
                        width: 100,
                        name: "Status",
                        render: (v: PurchaseOrderItem, col: any) => {
                            if(v.qtyBackOrdered > 0) {
                                return <span style={{color: red["800"], textDecoration: "underline"}}>{v.qtyBackOrdered} on backorder</span>
                            }

                            if(v.qtyPendingReceiving > 0) {
                                return <span style={{color: red["800"], textDecoration: "underline"}}>{v.qtyPendingReceiving} pending receiving</span>
                            }

                            if(v.qtyOutstanding > 0) {
                                return `${v.qtyOutstanding} outstanding`;
                            }

                            return <span style={{color: green["800"]}}>Received</span>
                        }
                    }),
                ]}
                fetch={ctx => getAll(ctx, offset => api.logistics.listPurchaseOrderItems({
                        offset,
                        purchaseOrder: props.order.id,
                    }), {
                        useLazy: true,
                    })}
                fetchDeps={[props.order.id]}
                onChange={v => api.logistics.updatePurchaseOrderItem(v)}
                noHorizontalScroller
                noFooter
            />
            <ItemsFooter value={props.order} />
        </CheckboxProvider>
        </>
    )
}

export function RowActions(props: {
    row: PurchaseOrderItem
}) {
    const snack = useSnackbar();
    const tbl = useContext(TableContext);

    return <RowActionsBase moreIcons={<div style={{marginTop: 5, marginLeft: 5}}><CheckboxContent id={props.row.id} /></div>}>
        {onClose => <>
            <MenuItem onClick={async () => {
                snack.loading();
                try {
                    await api.logistics.updatePurchaseOrderItem(Object.assign({}, props.row, {
                        archived: true,
                    }));

                    snack.success("Removed");

                    purchasingPopoutPanel.sendToClient("refresh-to-source");
                    purchaseOrderItemsChanged.emit(props.row.purchaseOrder);
                    await tbl.reload(true);
                } catch (e: any) {
                    snack.error(e.toString());
                }
                onClose();
            }}>
                <ListItemText style={{color: red["800"]}}>Remove</ListItemText>
            </MenuItem>
        </>}
    </RowActionsBase>
}

export function BulkActions() {
    const confirm = useContext(ConfirmContext);
    const snack = useSnackbar();
    const edit = useContext(EditContext);

    return (
        <MultiSelectRowHeader>
            {(close, ids, reset) => {
                return (
                    <>
                        <MenuItem color="error" onClick={async () => {
                            const ok = await confirm.confirm("Are you sure you want to remove all selected items?")
                            if(!ok) return;

                            close();

                            try {
                                snack.loading();

                                let list: PurchaseOrderItem[] = [];

                                edit.dataRef.current.forEach((g: Group<PurchaseOrderItem>) => {
                                    g.rows.forEach(r => {
                                        if(ids.indexOf(r.id) !== -1) {
                                            list.push(Object.assign({}, r, {
                                                archived: true,
                                            }))
                                        }
                                    })
                                })

                                await api.logistics.updatePurchaseOrderItemBulk(list);
                                purchasingPopoutPanel.sendToClient("refresh-to-source");
                                if(list.length > 0) purchaseOrderItemsChanged.emit(list[0].purchaseOrder);
                                await edit.reload();
                                snack.success("Updated");
                                reset();
                            } catch (e: any) {
                                snack.error(e.toString());
                            }
                        }}>
                            <ListItemText style={{color: red["800"]}}>Remove All</ListItemText>
                        </MenuItem>
                    </>
                )
            }}
        </MultiSelectRowHeader>
    )
}